import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AdvertiserReportItem } from '../../../../../app/api'
import { BpButton } from '../../../../../shared/components/buttons/bp-button/BpButton'
import { IconLink } from '../../../../../shared/components/icons/IconLink'
import { IconButton } from '../../../../../shared/components/buttons/icon-button/IconButton'
import { TriangleButton } from '../../../../../shared/components/buttons/triangle/TriangleButton'
import { BpTooltip } from '../../../../../shared/components/cards/tooltip/BpTooltip'
import { InfoField } from '../ui/InfoField'
import { NotAvailable } from '../ui/NotAvailable'
import { SectionCard } from '../ui/SectionCard'
import { IconVerified } from '../../../../../shared/components/icons/IconVerified'
import { Switch } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../../app/store/hooks/Redux'
import {
  setAdOverviewActiveTab,
  TAdOverviewTabs,
} from '../../../../ad-overview/model/adOverviewSlice'
import { AdvertiserReportFilterInterface } from '../../../model/types/AdvertiserReportFilter.interface'
import { selectAdvertiserReportFilters } from '../../../model/paidResultsSlice'
import { adOverviewLink } from './ReportLinks'

type Ad = AdvertiserReportItem['ads'][number] & { shopping_ad_preview_url?: string | null }

interface ILatestAdsTabProps {
  ads: AdvertiserReportItem['ads']
  shoppingAds: AdvertiserReportItem['shopping_ads']
  advertiser: string
}

const ImagePreview = ({ src }: { src: string }) => {
  const [imagePlug, setImagePlug] = React.useState(false)

  useEffect(() => {
    setImagePlug(false)
  }, [src])

  return imagePlug ? (
    <div className={'h-fit min-w-[67px] max-w-[67px] rounded-[6px] bg-aqua-300/5'}>
      <div className={'flex h-[67px] items-center justify-center text-[10px] text-aqua-300'}>
        no image
      </div>
    </div>
  ) : (
    <div className={'min-w-[67px] max-w-[67px]'}>
      <img
        alt={''}
        className={'w-full rounded-[6px]'}
        src={src}
        onError={() => setImagePlug(true)}
      />
    </div>
  )
}

export const LatestAdsTab = ({ ads, shoppingAds = [], advertiser }: ILatestAdsTabProps) => {
  const { t } = useTranslation()
  const filters = useAppSelector<AdvertiserReportFilterInterface>(selectAdvertiserReportFilters)
  const [isShoppingAds, setIsShoppingAds] = useState<boolean>(
    ads.length ? false : !!shoppingAds.length,
  )
  const dispatch = useAppDispatch()
  const allDomainAdsLink = adOverviewLink(filters, { adDomain: advertiser }, dispatch)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const currentAds = isShoppingAds ? shoppingAds : ads
  const selectedAd: Ad | undefined = currentAds[selectedIndex]

  const handlePrev = () => setSelectedIndex((i) => Math.max(0, i - 1))
  const handleNext = () => setSelectedIndex((i) => Math.min(currentAds.length - 1, i + 1))

  return (
    <div className={'flex flex-col gap-[16px] border-t border-t-[#E5E1EA] p-[14px]'}>
      {(!!ads.length || !!shoppingAds.length) && (
        <div className={'flex items-center justify-between'}>
          <div className={'flex items-center gap-[16px]'}>
            <h3 className={'text-[20px] font-semibold'}>
              {t('pages.paidResults.adCurrentOf', {
                current: selectedIndex + 1,
                of: currentAds.length,
              })}
            </h3>
            <div className={'flex gap-[8px]'}>
              <IconButton disabled={selectedIndex === 0} size='lg' onClick={handlePrev}>
                <TriangleButton color={'#A059FF'} position='left' />
              </IconButton>
              <IconButton
                disabled={selectedIndex === currentAds.length - 1}
                size='lg'
                onClick={handleNext}
              >
                <TriangleButton color={'#A059FF'} position='right' />
              </IconButton>
            </div>
          </div>
          {!!ads.length && !!shoppingAds.length && (
            <div className={'flex items-center gap-[8px]'}>
              <Switch
                checked={isShoppingAds}
                onChange={() => {
                  setIsShoppingAds((prevState) => !prevState)
                  setSelectedIndex(0)
                }}
              />
              {t('pages.paidResults.buttons.showShoppingAds')}
            </div>
          )}
        </div>
      )}
      {/* не помещаются — карточки переносятся; 368px — два поля Advertiser по 160px, зазор и отступы карточки */}
      <div className={'flex flex-wrap gap-[8px] [&>*]:min-w-[340px] [&>*]:flex-1'}>
        <SectionCard title={t('pages.paidResults.advertiser')}>
          {selectedAd?.advertiser ? (
            <div className={'flex flex-col gap-[16px]'}>
              <InfoField
                value={
                  <>
                    {selectedAd.advertiser.name}
                    {selectedAd.advertiser.verified && (
                      <span>
                        <IconVerified className='ml-1 inline w-[16px]' color='#00BD7B' />
                      </span>
                    )}
                  </>
                }
                label={t('pages.paidResults.name')}
              />
              <div className={'flex gap-[16px]'}>
                <InfoField
                  className={'min-w-[160px]'}
                  label={t('pages.paidResults.location')}
                  value={selectedAd.advertiser.location}
                />
                <InfoField
                  value={
                    selectedAd.advertiser.verified
                      ? selectedAd.advertiser.verified_by
                      : t('pages.paidResults.notVerified')
                  }
                  className={'min-w-[160px]'}
                  label={t('pages.paidResults.verifiedBy')}
                />
              </div>
              <div>
                <p className={'text-[14px] text-aqua'}>
                  {t('pages.paidResults.adsTransparencyCenter')}
                </p>
                <p className={'text-focus'}>
                  <a
                    className={'font-medium'}
                    href={selectedAd.advertiser.transparency_url}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {selectedAd.advertiser.transparency_url?.includes('google') ? 'Google' : 'Bing'}
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <NotAvailable>
              <BpTooltip title={t('pages.paidResults.notAvailableTooltip')} />
            </NotAvailable>
          )}
        </SectionCard>
        <SectionCard title={t('pages.paidResults.latestAdExample')}>
          {selectedAd ? (
            <div className={'flex h-full gap-[16px]'}>
              {!!selectedAd.shopping_ad_preview_url && (
                <ImagePreview src={selectedAd.shopping_ad_preview_url} />
              )}
              <div className={'flex h-full flex-col gap-[16px]'}>
                <div className={'flex flex-1 flex-col gap-[4px]'}>
                  <p
                    className={'text-[14px] font-medium leading-[20px] text-focus'}
                    dangerouslySetInnerHTML={{ __html: selectedAd.title ?? '' }}
                  ></p>
                  <p
                    className={'text-[14px] font-medium leading-[18px]'}
                    dangerouslySetInnerHTML={{ __html: selectedAd.text ?? '' }}
                  ></p>
                  <p
                    className={'text-[14px] font-medium text-[#258A00]'}
                    dangerouslySetInnerHTML={{ __html: selectedAd.url ?? '' }}
                  ></p>
                </div>
                <div>
                  <p className={'text-[14px] text-aqua-300'}>
                    {t('pages.paidResults.screenshots')}:{' '}
                    <a
                      className={'font-medium text-default'}
                      href={selectedAd.search_screen_url ?? undefined}
                      rel='noreferrer'
                      target={'_blank'}
                    >
                      {t('pages.paidResults.buttons.serp')}
                    </a>
                    ,{' '}
                    <a
                      className={'font-medium text-default'}
                      href={selectedAd.link_screen_url ?? undefined}
                      rel='noreferrer'
                      target={'_blank'}
                    >
                      {t('pages.paidResults.buttons.landingPage')}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <NotAvailable />
          )}
        </SectionCard>
        <SectionCard title={'Details'}>
          {selectedAd ? (
            <div className={'flex gap-[16px] overflow-hidden'}>
              <div className={'flex min-w-[140px] flex-col gap-[16px]'}>
                <InfoField
                  showTooltip
                  label={t('pages.paidResults.keyword')}
                  value={selectedAd.keyword}
                />
                <InfoField
                  label={t('pages.paidResults.adPosition')}
                  value={selectedAd.ad_position}
                />
                <InfoField label={t('pages.paidResults.country')} value={selectedAd.country} />
              </div>
              <div className={'flex flex-col gap-[16px] overflow-hidden'}>
                <InfoField
                  label={t('pages.paidResults.searchEngine')}
                  value={selectedAd.search_engine}
                />
                <InfoField label={t('pages.paidResults.lastFound')} value={selectedAd.last_found} />
                <InfoField
                  showTooltip
                  label={t('pages.paidResults.affiliateId')}
                  value={selectedAd.affiliate_id}
                />
              </div>
            </div>
          ) : (
            <NotAvailable />
          )}
        </SectionCard>
      </div>
      <div className={'flex w-full justify-center'}>
        <Link
          to={allDomainAdsLink.to}
          onClick={() => {
            allDomainAdsLink.onClick()

            if (isShoppingAds) {
              dispatch(setAdOverviewActiveTab(t('pages.adOverview.shoppingAds') as TAdOverviewTabs))
            } else {
              dispatch(setAdOverviewActiveTab(t('pages.adOverview.ads') as TAdOverviewTabs))
            }
          }}
        >
          <BpButton className={'uppercase'} iconLeft={<IconLink />}>
            {t('pages.paidResults.allDomainAds')}
          </BpButton>
        </Link>
      </div>
    </div>
  )
}
